import AppInfoFooter from "@/components/AppInfoFooter"
import Button from "@/components/Button"
import InputErrorMessage from "@/components/InputErrorMessage"
import { EmailInput, PasswordInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Link from "@/components/Link"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useLogInMutation } from "@/data/queries"
import { useAuthTokensAtom } from "@/data/store"
import { backendErrorCodeFor } from "@/model/BackendErrors"
import { ROUTES } from "@/routes"
import { loginSchema } from "@/schemas/zod-schemas"
import cn from "@/utils/cn"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Navigate, useLocation, useNavigate } from "react-router"

type LoginPageStateParams = {
  email?: string
}

type LoginPageFormData = {
  email: string
  password: string
}

const LoginPage = () => {
  const locationState = useLocation().state as LoginPageStateParams
  const [authTokens] = useAuthTokensAtom()
  const navigate = useNavigate()
  const {
    mutateAsync: logIn,
    isPending,
    error: logInError,
  } = useLogInMutation()

  const { register, handleSubmit, getValues, formState } =
    useForm<LoginPageFormData>({
      mode: "all",
      defaultValues: { email: locationState?.email ?? "", password: "" },
      resolver: zodResolver(loginSchema),
    })

  const isLoggedIn = authTokens !== null
  const logInErrorCode = backendErrorCodeFor(logInError)
  const { errors } = formState

  const onSubmit = async (formData: LoginPageFormData) => {
    await logIn(formData)
    navigate(ROUTES.POPULATION.ROOT, { replace: true })
  }

  return (
    <>
      {isLoggedIn && <Navigate to={ROUTES.POPULATION.ROOT} replace={true} />}
      <Page>
        <div className="flex h-full flex-col justify-between">
          <div
            className={cn(
              "mx-auto mt-10 w-full max-w-[460px] rounded-xl bg-white px-10 py-12 shadow-2xl"
            )}
          >
            <h2 className="mb-4 text-center text-3xl font-bold text-primary/90">
              Login to your account
            </h2>
            <form
              className="mt-12 flex flex-col gap-5"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="flex flex-col gap-1">
                <InputLabel htmlFor="email" className="ml-1">
                  Email
                </InputLabel>
                <EmailInput
                  id="email"
                  autoComplete="username"
                  disabled={isPending}
                  {...register("email")}
                  placeholder="Email address"
                  isError={!!errors?.email?.message}
                />
                <InputErrorMessage
                  message={errors?.email?.message}
                  className="ml-1"
                />
              </div>

              <div className="flex flex-col gap-1">
                <InputLabel htmlFor="password" className="ml-1">
                  Password
                </InputLabel>
                <PasswordInput
                  id="password"
                  autoComplete={"current-password"}
                  disabled={isPending}
                  {...register("password")}
                  placeholder="Password"
                  isError={!!errors?.password?.message}
                />
                <InputErrorMessage
                  message={errors?.password?.message}
                  className="ml-1"
                />
              </div>

              <div className="ml-1 flex">
                <Link
                  to={ROUTES.FORGOT_PASSWORD}
                  state={{ email: getValues("email") }}
                  className={cn(
                    "inline-flex",
                    isPending ? "pointer-events-none" : ""
                  )}
                >
                  Forgot password?
                </Link>
              </div>

              {logInErrorCode && (
                <div className="my-4 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
                  <span className="material-icons me-4 text-red-600">
                    error_outline
                  </span>
                  <p className="text-red-600">
                    {logInErrorCode === "USER_DISABLED"
                      ? "This account is inactive, please contact Bloomlife Support"
                      : logInErrorCode === "LOGIN_FAILED"
                        ? "Unknown email/password combination"
                        : "Unable to log in"}
                  </p>
                </div>
              )}
              <Button
                variant="primary"
                type="submit"
                disabled={isPending}
                className="flex w-full gap-3 py-2 text-base"
              >
                {isPending && <Spinner />}
                Login
              </Button>
            </form>
          </div>
          <AppInfoFooter className="mt-auto p-4" />
        </div>
      </Page>
    </>
  )
}

export default LoginPage
